const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.cricfantasypros.com/api";

// Helper function to handle API responses
const handleResponse = async (response: Response) => {
  console.log('🔍 API: Response status:', response.status);
  console.log('🔍 API: Response headers:', Object.fromEntries(response.headers.entries()));
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.log('🔥 API: Error response:', errorData);
    
    // Rate limited – show clear message
    if (response.status === 429) {
      const msg = errorData?.message || 'Too many requests. Please wait a minute and try again.';
      throw new Error(msg);
    }
    
    // Handle token expiration or JWT errors
    if (response.status === 403 || errorData.message === 'Invalid or expired token' || errorData.message === 'jwt malformed') {
      alert('Your session has expired or you are not authorized. Please log in again as admin.');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
      throw new Error('Session expired or not authorized');
    }
    
    const errText =
      errorData?.message || errorData?.error || (typeof errorData?.details === 'string' ? errorData.details : '');
    throw new Error(errText || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  console.log('🔍 API: Success response:', data);
  return data;
};

// Helper function to refresh token
const refreshToken = async (): Promise<boolean> => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.log('❌ No refresh token available');
      return false;
    }

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      console.log('✅ Token refreshed successfully');
      return true;
    } else {
      console.log('❌ Token refresh failed');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      return false;
    }
  } catch (error) {
    console.error('❌ Token refresh error:', error);
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    return false;
  }
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  console.log('🔍 API: Getting auth headers, token exists:', !!token);
  if (token) {
    console.log('🔍 API: Token length:', token.length);
    console.log('🔍 API: Token starts with:', token.substring(0, 20) + '...');
  }
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const apiService = {
  // Authentication
  login: async (emailOrUsername: string, password: string) => {
    console.log('🔍 API: Making login request to:', `${API_BASE_URL}/auth/login`);
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailOrUsername, password })
    });
    return handleResponse(response);
  },

  register: async (display_name: string, email: string, password: string, firstName: string, lastName: string, dateOfBirth: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        email, 
        password, 
        display_name, 
        first_name: firstName, 
        last_name: lastName, 
        date_of_birth: dateOfBirth 
      })
    });
    return handleResponse(response);
  },

  forgotPassword: async (email: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return handleResponse(response);
  },

  refreshToken: async (refreshToken: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });
    return handleResponse(response);
  },

  resetPassword: async (token: string, newPassword: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword })
    });
    return handleResponse(response);
  },

  // Tournaments
  getTournaments: async () => {
    const response = await fetch(`${API_BASE_URL}/schedule/tournaments`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getLiveTournaments: async () => {
    const response = await fetch(`${API_BASE_URL}/tournaments/live`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getLiveTournamentById: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/live/${tournamentId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getTournamentTeams: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/live/${tournamentId}/teams`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getTournamentPlayers: async (tournamentId: number, teamId?: number) => {
    const url = teamId 
      ? `${API_BASE_URL}/tournaments/live/${tournamentId}/players?teamId=${teamId}`
      : `${API_BASE_URL}/tournaments/live/${tournamentId}/players`;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getTournamentMatches: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/live/${tournamentId}/matches`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  /** Public standings from latest admin snapshot (Wikipedia / optional sources). */
  getLiveTournamentPointsTable: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/live/${tournamentId}/points-table`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getAllMatches: async () => {
    const response = await fetch(`${API_BASE_URL}/matches`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getTournamentDetails: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/schedule/tournaments/${tournamentId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Fantasy Teams
  getMyTeams: async () => {
    const response = await fetch(`${API_BASE_URL}/transfer/teams`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getAvailablePlayers: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/transfer/players?tournament_id=${tournamentId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  createTeam: async (tournamentId: number, teamData: any) => {
    const response = await fetch(`${API_BASE_URL}/transfer/teams`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tournament_id: tournamentId, ...teamData })
    });
    return handleResponse(response);
  },

  updateTeam: async (teamId: number, teamData: any) => {
    const response = await fetch(`${API_BASE_URL}/transfer/teams/${teamId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(teamData)
    });
    return handleResponse(response);
  },

  // Transfers
  getTransfers: async () => {
    const response = await fetch(`${API_BASE_URL}/transfer/transfers`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  makeTransfer: async (transferData: any) => {
    const response = await fetch(`${API_BASE_URL}/transfer/transfers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(transferData)
    });
    return handleResponse(response);
  },

  // Standings
  getStandings: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/standings/${tournamentId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Matches
  getMatches: async (tournamentId?: number) => {
    const url = tournamentId 
      ? `${API_BASE_URL}/tournaments/live/${tournamentId}/matches`
      : `${API_BASE_URL}/schedule/upcoming`;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Pick'em Predictions
  getPickemPredictions: async (matchId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/predictions/${matchId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Get user predictions for a league
  getUserPickemPredictions: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/predictions`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getAllPickemPredictions: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/all-predictions`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  submitPickemPrediction: async (matchId: number, prediction: any) => {
    const response = await fetch(`${API_BASE_URL}/pickem/predictions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ match_id: matchId, ...prediction })
    });
    return handleResponse(response);
  },

  // Pick'em Tournaments
  getPickemTournaments: async () => {
    const response = await fetch(`${API_BASE_URL}/pickem/tournaments`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getPickemTournamentDetails: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/tournaments/${tournamentId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Pick'em Leagues
  getPickemLeagues: async () => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  createPickemLeague: async (leagueData: any) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(leagueData)
    });
    return handleResponse(response);
  },



  // Eliminator Predictions
  joinEliminatorTournament: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/join/${tournamentId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Old eliminator system - renamed to avoid conflict
  getEliminatorUserPredictions: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/user/${tournamentId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  submitEliminatorPrediction: async (matchId: number, tournamentId: number, prediction: any) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/predict`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ match_id: matchId, tournament_id: tournamentId, ...prediction })
    });
    return handleResponse(response);
  },

  getEliminatorStatus: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/status/${tournamentId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getEliminatorLeaderboard: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/leaderboard/${tournamentId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getEliminatorStats: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/stats/${tournamentId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // User Profile
  getUserProfile: async () => {
    console.log('🔍 API: Making getUserProfile request');
    const headers = getAuthHeaders();
    console.log('🔍 API: Auth headers:', headers);
    
    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
      headers: headers
    });
    console.log('🔍 API: Profile response status:', response.status);
    
    const data = await handleResponse(response);
    console.log('🔍 API: Profile response data:', data);
    return data; // Return the data directly since backend returns profile data directly
  },

  updateUserProfile: async (profileData: any) => {
    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(profileData)
    });
    return handleResponse(response);
  },

  // Notifications
  getNotifications: async () => {
    const response = await fetch(`${API_BASE_URL}/notifications`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  markNotificationRead: async (notificationId: number) => {
    const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Health check
  healthCheck: async () => {
    const response = await fetch(`${API_BASE_URL.replace('/api', '')}/`);
    return handleResponse(response);
  },

  // Admin - Tournaments
  getImportedTournamentsAdmin: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getVisibleTournamentsAdmin: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/visible`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  updateTournamentAdmin: async (tournamentId: number, updates: any) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });
    return handleResponse(response);
  },

  /** Full live_tournament row (admin); includes pickem_phase_groups. */
  getAdminTournamentById: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updatePickemPhaseGroups: async (
    tournamentId: number,
    pickem_phase_groups: { phases: Array<{ id: string; label: string; match_ids: string[] }> } | null
  ) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/pickem-phase-groups`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ pickem_phase_groups })
    });
    return handleResponse(response);
  },

  /** H2H settings for league creation (any authenticated user). Use this in Create League modal. */
  getTournamentH2hSettingsForLeagueCreation: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/live/${tournamentId}/h2h-settings`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  /** H2H settings (admin only). Use for admin tournament H2H tab. */
  getTournamentH2hSettings: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/h2h`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateTournamentH2hSettings: async (tournamentId: number, settings: { h2h_enabled?: boolean; h2h_allowed_modes?: string[]; h2h_allowed_lengths?: number[] }) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/h2h`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(settings)
    });
    return handleResponse(response);
  },

  getH2hLeaguePlayers: async (leagueId: number, matchId?: string) => {
    const params = matchId ? `?matchId=${encodeURIComponent(matchId)}` : '';
    const response = await fetch(`${API_BASE_URL}/h2h/leagues/${leagueId}/players${params}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getH2hLeagueConfig: async () => {
    const response = await fetch(`${API_BASE_URL}/h2h/leagues/config`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getH2hLeagueState: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/h2h/leagues/${leagueId}/state`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  createTournamentAdmin: async (tournamentData: any) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(tournamentData)
    });
    return handleResponse(response);
  },

  getTournamentSeriesInfo: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/series_info`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Admin - CricAPI Tournaments
  getCricAPITournaments: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/cricapi/tournaments`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  // Admin - Import CricAPI Tournaments
  importCricAPITournaments: async (tournamentIds: string[]) => {
    const response = await fetch(`${API_BASE_URL}/admin/cricapi/import`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tournamentIds })
    });
    return handleResponse(response);
  },
  // Admin - Clear CricAPI Cache
  clearCricAPICache: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/cricapi/cache`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  // Admin - Fetch Custom CricAPI Endpoint
  fetchCricAPIEndpoint: async (endpoint: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/cricapi/fetch${endpoint}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  
  // Admin - Fetch Series Squad
  fetchSeriesSquad: async (seriesId: string, tournamentId?: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/cricapi/series_squad`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ seriesId, tournamentId })
    });
    return handleResponse(response);
  },

  // Admin - Import Series Info
  importSeriesInfo: async (tournamentId: number, seriesId: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/import_series_info`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ seriesId }),
    });
    return handleResponse(response);
  },

  // Admin - Import Series Squad
  importSeriesSquad: async (tournamentId: number, seriesId: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/import_series_squad`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ seriesId })
    });
    return handleResponse(response);
  },

  // Admin - Run Pickem/Budget auto-result check now (same as 30-min cron). Returns { checked, updated, detail }.
  runAutoResultCheck: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/run-auto-result`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
  
  // Admin - Toggle Teams Visibility
  toggleTeamsVisibility: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/toggle_teams_visibility`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  
  // Admin - Toggle Schedule Visibility
  toggleScheduleVisibility: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/toggle_schedule_visibility`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Admin - Update Visible Teams
  updateVisibleTeams: async (tournamentId: number, visibleTeams: string[]) => {
    console.log('🔍 Frontend: Updating visible teams:', { tournamentId, visibleTeams });
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/update_visible_teams`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ visibleTeams })
    });
    const result = await handleResponse(response);
    console.log('🔍 Frontend: Update visible teams result:', result);
    return result;
  },

  // Admin - Update Visible Matches
  updateVisibleMatches: async (tournamentId: number, visibleMatches: string[]) => {
    console.log('🔍 Frontend: Updating visible matches:', { tournamentId, visibleMatches });
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/update_visible_matches`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ visibleMatches })
    });
    const result = await handleResponse(response);
    console.log('🔍 Frontend: Update visible matches result:', result);
    return result;
  },

  // Admin - Update Visible Players
  updateVisiblePlayers: async (tournamentId: number, visiblePlayers: string[]) => {
    console.log('🔍 Frontend: Updating visible players:', { tournamentId, visiblePlayers });
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/update_visible_players`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ visiblePlayers })
    });
    const result = await handleResponse(response);
    console.log('🔍 Frontend: Update visible players result:', result);
    return result;
  },

  // Admin - Update Foreign Players
  updateForeignPlayers: async (tournamentId: number, foreignPlayers: string[]) => {
    console.log('🔍 Frontend: Updating foreign players:', { tournamentId, foreignPlayers });
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/update_foreign_players`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ foreignPlayers })
    });
    const result = await handleResponse(response);
    console.log('🔍 Frontend: Update foreign players result:', result);
    return result;
  },

  // Admin - Get Visible Items
  getVisibleItems: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/visible_items`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Admin - Add Manual Teams
  addManualTeams: async (tournamentId: number, teams: any[]) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/manual/teams`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ teams })
    });
    return handleResponse(response);
  },

  // Admin - Add Manual Schedule
  addManualSchedule: async (tournamentId: number, matches: any[]) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/manual/schedule`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ matches })
    });
    return handleResponse(response);
  },

  // Admin - Add Manual Squad
  addManualSquad: async (tournamentId: number, squad: any[]) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/manual/squad`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ squad })
    });
    return handleResponse(response);
  },

  // Admin: Delete team from tournament
  deleteTeamFromTournament: async (tournamentId: string | number, shortname: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/team/${shortname}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Admin: Delete match from schedule
  deleteMatchFromTournament: async (tournamentId: string | number, matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/match/${matchId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Admin: Delete player from squad
  deletePlayerFromTournament: async (tournamentId: string | number, playerId: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/player/${playerId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Fantasy Leagues
  createFantasyLeague: async (leagueData: any) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(leagueData)
    });
    return handleResponse(response);
  },

  getFantasyLeagues: async () => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getMyLeagues: async () => {
    const response = await fetch(`${API_BASE_URL}/tournaments/my-leagues`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getFantasyLeaguesByTournament: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/live/${tournamentId}/leagues`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  joinFantasyLeague: async (leagueId: number, joinCode?: string) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/${leagueId}/join`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ join_code: joinCode })
    });
    return handleResponse(response);
  },

  joinLeagueByCode: async (joinCode: string, passcode?: string) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/join-by-code`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ join_code: joinCode, passcode: passcode })
    });
    return handleResponse(response);
  },

  // Budget League API Functions
  getBudgetLeagueConfig: async () => {
    const response = await fetch(`${API_BASE_URL}/budget-league/config`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Admin only: Populate tournament_players from current series squad (so Budget League shows all squads e.g. after re-import)
  populateBudgetLeagueTournamentPlayers: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/tournaments/${tournamentId}/populate`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBudgetLeaguePlayers: async (tournamentId: number, filters?: any) => {
    const params = new URLSearchParams();
    if (filters?.role) params.append('role', filters.role);
    if (filters?.team) params.append('team', filters.team);
    if (filters?.maxCredits) params.append('maxCredits', filters.maxCredits.toString());
    
    const response = await fetch(`${API_BASE_URL}/budget-league/tournaments/${tournamentId}/players?${params}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBudgetLeaguePlayerTournamentPoints: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/tournaments/${tournamentId}/player-points`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBudgetLeagueEnabledBoosters: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/tournaments/${tournamentId}/enabled-boosters`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBudgetLeagueUsedBoosters: async (teamId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/used-boosters`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  createBudgetLeagueTeam: async (teamData: any) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(teamData)
    });
    return handleResponse(response);
  },

  getBudgetLeagueTeam: async (tournamentId: number, leagueId: number) => {
    console.log('🔍 API: Making getBudgetLeagueTeam request');
    console.log('🔍 API: URL:', `${API_BASE_URL}/budget-league/teams/${tournamentId}/${leagueId}`);
    console.log('🔍 API: Headers:', getAuthHeaders());
    
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${tournamentId}/${leagueId}`, {
      headers: getAuthHeaders()
    });
    
    console.log('🔍 API: Response status:', response.status);
    console.log('🔍 API: Response ok:', response.ok);
    
    return handleResponse(response);
  },

  makeBudgetLeagueTransfers: async (transferData: any) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/transfers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(transferData)
    });
    return handleResponse(response);
  },

  getBudgetLeagueStandings: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/leagues/${leagueId}/standings`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBudgetLeagueMatches: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/leagues/${leagueId}/matches`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBudgetLeagueMatchStandings: async (leagueId: number, matchId: string | number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/leagues/${leagueId}/matches/${matchId}/standings`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBudgetLeagueMatchSnapshot: async (teamId: number, matchId: string | number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/matches/${matchId}/snapshot`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getMostRecentSnapshot: async (teamId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/most-recent-snapshot`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBudgetLeagueMatchPlayerPoints: async (matchId: string | number, leagueId?: number) => {
    const url = new URL(`${API_BASE_URL}/budget-league/matches/${matchId}/player-points`);
    if (leagueId) {
      url.searchParams.append('leagueId', leagueId.toString());
    }
    const response = await fetch(url.toString(), {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBudgetLeagueTeamRecentMatchPoints: async (teamId: number, leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/recent-match-points?leagueId=${leagueId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Smart Transfer System - Staging APIs
  getTeamStagingState: async (teamId: number, matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/staging/${matchId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateTeamStagingState: async (
    teamId: number,
    matchId: string,
    stagedPlayers: any[],
    captainId: number | null,
    viceCaptainId: number | null,
    boosterOptions?: { applied_booster_id?: number | null; triple_threat_player_id?: number | null; impact_player_id?: number | null }
  ) => {
    const body: any = { staged_players: stagedPlayers, captain_id: captainId, vice_captain_id: viceCaptainId };
    if (boosterOptions) {
      if (boosterOptions.applied_booster_id != null) body.applied_booster_id = boosterOptions.applied_booster_id;
      if (boosterOptions.triple_threat_player_id != null) body.triple_threat_player_id = boosterOptions.triple_threat_player_id;
      if (boosterOptions.impact_player_id != null) body.impact_player_id = boosterOptions.impact_player_id;
    }
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/staging/${matchId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });
    return handleResponse(response);
  },

  revertTeamStaging: async (teamId: number, matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/staging/${matchId}/revert`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  lockTeamForMatch: async (teamId: number, matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/staging/${matchId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getTeamMatchNavigation: async (teamId: number, leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/match-navigation?leagueId=${leagueId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getFantasyLeague: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/${leagueId}/league`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getFantasyLeagueParticipants: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/${leagueId}/participants`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getFantasyTeamMatchPoints: async (teamId: number) => {
    const response = await fetch(`${API_BASE_URL}/fantasy/teams/${teamId}/match-points`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBudgetLeagueTeamMatchPoints: async (teamId: number, matchId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/matches/${matchId}/points`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  calculateBudgetLeagueMatchPoints: async (matchId: number, tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/matches/${matchId}/calculate-points`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tournamentId })
    });
    return handleResponse(response);
  },

  validateBudgetLeagueTeam: async (tournamentId: number, players: any[]) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/validate-team`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tournamentId, players })
    });
    return handleResponse(response);
  },

  getBudgetLeagueTransferHistory: async (teamId: number) => {
    const response = await fetch(`${API_BASE_URL}/budget-league/teams/${teamId}/transfers`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  leaveFantasyLeague: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/${leagueId}/leave`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  removeUserFromLeague: async (leagueId: number, participantId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/${leagueId}/participants/${participantId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  promoteUserToManager: async (leagueId: number, participantId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/${leagueId}/participants/${participantId}/promote`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  demoteUserFromManager: async (leagueId: number, participantId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/${leagueId}/participants/${participantId}/demote`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  deleteFantasyLeague: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/${leagueId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Pick'em specific methods
  getPickemLeague: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getPickemParticipants: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/participants`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getPickemMatches: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/matches?t=${Date.now()}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  makePickemPrediction: async (
    leagueId: number,
    matchId: string,
    predictedWinner: string,
    totalRuns?: number,
    momPlayerId?: string | number,
    momPlayerName?: string,
    winMarginPrediction?: string,
    transactionId?: string
  ) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/predictions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        matchId,
        predictedWinner,
        totalRuns,
        momPlayerId,
        momPlayerName,
        winMarginPrediction,
        transactionId
      })
    });
    return handleResponse(response);
  },

  getPickemUserActivity: async (leagueId: number, limit = 100) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/activity?limit=${limit}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updatePickemPickConfirmationEmail: async (leagueId: number, enabled: boolean) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/pick-confirmation-email`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ enabled })
    });
    return handleResponse(response);
  },

  sendPickemPickConfirmationEmail: async (
    leagueId: number,
    body: { transactionId: string; ianaTimezone?: string }
  ) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/pick-confirmation-email/send`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });
    return handleResponse(response);
  },

  getPickemStandings: async (
    leagueId: number,
    opts?: { scope?: 'cumulative' | 'phase'; phase_id?: string | null }
  ) => {
    const q = new URLSearchParams();
    if (opts?.scope === 'phase') q.set('scope', 'phase');
    if (opts?.phase_id) q.set('phase_id', opts.phase_id);
    const qs = q.toString();
    const response = await fetch(
      `${API_BASE_URL}/pickem/leagues/${leagueId}/standings${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getPickemStandingsUserBreakdown: async (
    leagueId: number,
    userId: number,
    opts?: { phase_id?: string | null }
  ) => {
    const q = new URLSearchParams();
    if (opts?.phase_id) q.set('phase_id', opts.phase_id);
    const qs = q.toString();
    const response = await fetch(
      `${API_BASE_URL}/pickem/leagues/${leagueId}/standings/users/${userId}/breakdown${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getPickemPhaseScoreMatrix: async (leagueId: number) => {
    const response = await fetch(
      `${API_BASE_URL}/pickem/leagues/${leagueId}/standings/phase-matrix`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  joinPickemLeague: async (leagueId: number, joinCode?: string, passcode?: string) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/join`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ joinCode, passcode })
    });
    return handleResponse(response);
  },

  joinPickemLeagueByCode: async (joinCode: string, passcode?: string) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/join-by-code`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ join_code: joinCode, passcode: passcode })
    });
    return handleResponse(response);
  },

  // League Settings
  getLeagueSettings: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/settings`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateLeagueSettings: async (leagueId: number, settings: any) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ settings })
    });
    return handleResponse(response);
  },

  /** Same lock buffer as pickem makePrediction (league_settings.lock_buffer_minutes, default 2). */
  getMatchLockLeagueSettings: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/match-lock/league/${leagueId}/settings`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  recalculateLeaguePoints: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/recalculate-points`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getLeagueStatus: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/status`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updatePickemDynamicScoring: async (leagueId: number, enabled: boolean) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/dynamic-scoring`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ enabled })
    });
    return handleResponse(response);
  },

  updatePickemMatchStartPickSummaryEmail: async (leagueId: number, enabled: boolean) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/match-start-pick-summary-email`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ enabled })
    });
    return handleResponse(response);
  },

  updatePickemAllPicksVisibility: async (leagueId: number, enabled: boolean) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/all-picks-visibility`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ enabled })
    });
    return handleResponse(response);
  },

  getPickemDynamicPreview: async (leagueId: number, matchId: string) => {
    const mid = encodeURIComponent(matchId);
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/matches/${mid}/dynamic-preview`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getLeagueStandingsCalculated: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/standings-calculated`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getStartedMatchPredictions: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/started-predictions`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Enhanced Pick'em Features
  getMatchSquadPlayers: async (leagueId: number, matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/matches/${matchId}/squad-players`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateMatchResults: async (leagueId: number, matchId: string, totalRuns: number, manOfMatchPlayerId: number, manOfMatchPlayerName: string) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/matches/${matchId}/results`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ totalRuns, manOfMatchPlayerId, manOfMatchPlayerName })
    });
    return handleResponse(response);
  },

  updatePickemPredictionResults: async (leagueId: number, matchId: string, actualWinner: string) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/matches/${matchId}/results`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ actualWinner })
    });
    return handleResponse(response);
  },

  getUserPredictions: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/predictions`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getAllPredictions: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/all-predictions`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getPickemPredictionActivityAuditAdmin: async (opts?: {
    leagueId?: number;
    userId?: number;
    matchId?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (opts?.leagueId != null) q.set('leagueId', String(opts.leagueId));
    if (opts?.userId != null) q.set('userId', String(opts.userId));
    if (opts?.matchId) q.set('matchId', String(opts.matchId));
    if (opts?.limit != null) q.set('limit', String(opts.limit));
    const qs = q.toString();
    const response = await fetch(
      `${API_BASE_URL}/admin/pickem/prediction-activity${qs ? `?${qs}` : ''}`,
      {
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  // Admin League Management
  getAllLeaguesAdmin: async (leagueType: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/leagues?type=${leagueType}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  deleteLeagueAdmin: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/leagues/${leagueId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  promoteLeagueManager: async (leagueId: number, userId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/leagues/${leagueId}/promote-manager`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userId })
    });
    return handleResponse(response);
  },

  // Admin League Details
  getLeagueParticipantsAdmin: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/leagues/${leagueId}/participants`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getLeagueMatchesAdmin: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/leagues/${leagueId}/matches`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getLeagueSettingsAdmin: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/leagues/${leagueId}/settings`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  removeLeagueParticipantAdmin: async (leagueId: number, participantId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/leagues/${leagueId}/participants/${participantId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateLeagueSettingsAdmin: async (leagueId: number, settings: any) => {
    const response = await fetch(`${API_BASE_URL}/admin/leagues/${leagueId}/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(settings)
    });
    return handleResponse(response);
  },

  removeLeagueAdmin: async (leagueId: number, userId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/leagues/${leagueId}/remove-admin`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userId })
    });
    return handleResponse(response);
  },

  // Tournament Match Management
  getTournamentMatchesAdmin: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/matches`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  importTournamentMatches: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/matches/import`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateTournamentMatchResult: async (tournamentId: number, matchId: string, matchData: any) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/matches/${matchId}/result`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(matchData)
    });
    return handleResponse(response);
  },

  updateTournamentMatchVenue: async (tournamentId: number, matchId: string, venue: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/matches/${matchId}/venue`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ venue })
    });
    return handleResponse(response);
  },

  scrapeTournamentPointsTable: async (
    tournamentId: number,
    body?: { cricbuzzUrl?: string; espnUrl?: string; wikipediaUrl?: string; afterMatchId?: string }
  ) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/points-table/scrape`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body || {})
    });
    return handleResponse(response);
  },

  /** Wikipedia schedule page (e.g. IPL) → tournament_match_results.venue by team pairing */
  scrapeTournamentScheduleVenues: async (
    tournamentId: number,
    url: string,
    dryRun?: boolean
  ) => {
    const response = await fetch(
      `${API_BASE_URL}/admin/tournaments/${tournamentId}/schedule/venues/scrape`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ url, dryRun: !!dryRun })
      }
    );
    return handleResponse(response);
  },

  getTournamentPointsSnapshots: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/points-table/snapshots`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getTournamentPointsTableConfig: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/points-table/config`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateTournamentPointsTableConfig: async (
    tournamentId: number,
    body: {
      aliases?: Record<string, string[]>;
      cricbuzzUrl?: string;
      espnUrl?: string;
      wikipediaUrl?: string;
    }
  ) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/points-table/config`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });
    return handleResponse(response);
  },

  updateMatchPickemPhase: async (
    tournamentId: number,
    matchId: string,
    pickem_phase: 'league' | 'playoff' | 'final'
  ) => {
    const mid = encodeURIComponent(matchId);
    const response = await fetch(
      `${API_BASE_URL}/admin/tournaments/${tournamentId}/matches/${mid}/pickem-phase`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ pickem_phase })
      }
    );
    return handleResponse(response);
  },

  fetchMatchDetails: async (tournamentId: number, matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/matches/${matchId}/fetch-details`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  fetchPOMFromUrl: async (tournamentId: number, matchId: string, espnScorecardUrl: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/matches/${matchId}/fetch-pom`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ espn_scorecard_url: espnScorecardUrl })
    });
    return handleResponse(response);
  },

  updateMatchEspnUrl: async (tournamentId: number, matchId: string, espnScorecardUrl: string | null) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/matches/${matchId}/espn-url`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ espn_scorecard_url: espnScorecardUrl || '' })
    });
    return handleResponse(response);
  },

  getMatchPlayers: async (tournamentId: number, matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/matches/${matchId}/players`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Fantasy Point Rules (admin)
  getFantasyPointRules: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/fantasy-point-rules`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateFantasyPointRules: async (tournamentId: number, rules: any) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/fantasy-point-rules`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(rules)
    });
    return handleResponse(response);
  },

  applyMatchPointAdjustments: async (tournamentId: number, matchId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/admin/tournaments/${tournamentId}/matches/${matchId}/apply-point-adjustments`,
      { method: 'POST', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  // Fantasy Point Rules (public – for Points Summary shown to users)
  getFantasyPointRulesPublic: async (tournamentId: number) => {
    const response = await fetch(
      `${API_BASE_URL}/tournaments/live/${tournamentId}/fantasy-point-rules`
    );
    return handleResponse(response);
  },

  // 🎯 Budget League Match Extension Functions
  getMatchExtensionStatus: async (matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/budget-league/matches/${matchId}/extension-status`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  extendMatchLockTime: async (matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/budget-league/matches/${matchId}/extend-lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  resetMatchLockTime: async (matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/admin/budget-league/matches/${matchId}/reset-lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  unlockMatchAndExtend: async (matchId: string, leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/budget-league/matches/${matchId}/unlock-and-extend`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ leagueId })
    });
    return handleResponse(response);
  },

  // 🏆 Eliminator League Functions
  getEliminatorLeague: async (leagueId: number) => {
    console.log('🔍 API: Calling getEliminatorLeague for leagueId:', leagueId);
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}`, {
      headers: getAuthHeaders()
    });
    console.log('🔍 API: getEliminatorLeague response status:', response.status);
    return handleResponse(response);
  },

  getEliminatorLeagueParticipants: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/participants`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getEliminatorMatches: async (leagueId: number) => {
    console.log('🔍 API: Calling getEliminatorMatches for leagueId:', leagueId);
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/matches`, {
      headers: getAuthHeaders()
    });
    console.log('🔍 API: getEliminatorMatches response status:', response.status);
    return handleResponse(response);
  },

  makeEliminatorPrediction: async (leagueId: number, matchId: string, predictedWinner: string) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/predict`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ matchId, predictedWinner })
    });
    return handleResponse(response);
  },

  getEliminatorPredictions: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/predictions`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getAllEliminatorPredictions: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/all-predictions`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getEliminatorSurvivors: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/survivors`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  checkEliminationStatus: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/elimination-status`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getEliminatorLeagueSettings: async (leagueId: number) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/settings`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateEliminatorLeagueSettings: async (leagueId: number, settings: any) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(settings)
    });
    return handleResponse(response);
  },

  joinEliminatorLeague: async (leagueId: number, joinCode?: string, passcode?: string) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/join`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ joinCode, passcode })
    });
    return handleResponse(response);
  },

  getDefaultEliminatorSettings: async () => {
    const response = await fetch(`${API_BASE_URL}/eliminator/default-settings`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateEliminatorPredictionResults: async (leagueId: number, matchId: string, actualWinner: string) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/update-results`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ matchId, actualWinner })
    });
    return handleResponse(response);
  },

  // Update league name
  updatePickemLeagueName: async (leagueId: number, name: string) => {
    const response = await fetch(`${API_BASE_URL}/pickem/leagues/${leagueId}/name`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name })
    });
    return handleResponse(response);
  },

  updateEliminatorLeagueName: async (leagueId: number, name: string) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/league/${leagueId}/name`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name })
    });
    return handleResponse(response);
  },

  // Generic fantasy league update methods (for budget_league, h2h, draft_league)
  updateFantasyLeagueName: async (leagueId: number, name: string) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/${leagueId}/name`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name })
    });
    return handleResponse(response);
  },

  updateFantasyLeagueMaxParticipants: async (leagueId: number, maxParticipants: number) => {
    const response = await fetch(`${API_BASE_URL}/tournaments/fantasy/${leagueId}/max-participants`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ max_participants: maxParticipants })
    });
    return handleResponse(response);
  },

  joinEliminatorLeagueByCode: async (joinCode: string, passcode?: string) => {
    const response = await fetch(`${API_BASE_URL}/eliminator/join-by-code`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ join_code: joinCode, passcode })
    });
    return handleResponse(response);
  },

  // Player Management for Budget Leagues
  updatePlayerCredits: async (tournamentId: number, playerId: string, credits: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/players/${playerId}/credits`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ credits })
    });
    return handleResponse(response);
  },

  bulkUpdatePlayerCredits: async (tournamentId: number, updates: Array<{playerId: string, credits: number}>) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/players/bulk-credits`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ updates })
    });
    return handleResponse(response);
  },

  getPlayerCredits: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/players/credits`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Tournament Booster Management
  getTournamentBoosters: async (tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/boosters`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateTournamentBooster: async (tournamentId: number, boosterId: number, isEnabled: boolean, maxUsagePerLeague: number = 1) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/boosters/${boosterId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ is_enabled: isEnabled, max_usage_per_league: maxUsagePerLeague })
    });
    return handleResponse(response);
  },

  bulkUpdateTournamentBoosters: async (tournamentId: number, boosters: any[]) => {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/boosters/bulk`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ boosters })
    });
    return handleResponse(response);
  },

  getAvailableBoosters: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/boosters/available`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Real-time Scoring API Methods
  startRealTimeUpdates: async (matchId: string, tournamentId: number) => {
    const response = await fetch(`${API_BASE_URL}/real-time-scoring/matches/${matchId}/start`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tournamentId })
    });
    return handleResponse(response);
  },

  stopRealTimeUpdates: async (matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/real-time-scoring/matches/${matchId}/stop`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getRealTimeStatus: async () => {
    const response = await fetch(`${API_BASE_URL}/real-time-scoring/matches/status`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getMatchRealTimeStatus: async (matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/real-time-scoring/matches/${matchId}/status`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  triggerManualUpdate: async (matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/real-time-scoring/matches/${matchId}/update`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  stopAllRealTimeUpdates: async () => {
    const response = await fetch(`${API_BASE_URL}/real-time-scoring/stop-all`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getRealTimeStats: async () => {
    const response = await fetch(`${API_BASE_URL}/real-time-scoring/stats`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getLiveFantasyPoints: async (matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/real-time-scoring/matches/${matchId}/fantasy-points`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getLivePlayerStats: async (matchId: string) => {
    const response = await fetch(`${API_BASE_URL}/real-time-scoring/matches/${matchId}/player-stats`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
};

export default apiService;
